/**
 * DNA Helix preloader shown on every page load.
 *
 * A rotating double helix rendered on canvas with the logo at center.
 * Fitting for clinical/pharma. Helix strands use brand primary + secondary
 * colors with glowing base-pair connections.
 *
 * Uses branding store for logo, app name, tagline, and colors.
 * Detects light/dark preference via matchMedia (no React context needed).
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { BRANDING } from '@/config/brandingDefaults'
import { useBrandingStore } from '@/stores/brandingStore'

interface PreloaderProps {
  onComplete: () => void
  duration?: number
}

// ── Helix renderer ───────────────────────────────────────────────

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return { r, g, b }
}

function useHelixCanvas(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  primaryColor: string,
  secondaryColor: string,
  prefersDark: boolean,
  isExiting: boolean,
) {
  const rafRef = useRef<number>(0)
  const startTimeRef = useRef<number>(performance.now())
  const exitStartRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      canvas.width = window.innerWidth * dpr
      canvas.height = window.innerHeight * dpr
      canvas.style.width = `${window.innerWidth}px`
      canvas.style.height = `${window.innerHeight}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    resize()
    window.addEventListener('resize', resize)

    const rgb1 = hexToRgb(primaryColor)
    const rgb2 = hexToRgb(secondaryColor)

    // Helix configuration
    const HELIX_POINTS = 80 // Points per strand
    const HELIX_HEIGHT_RATIO = 0.7 // % of screen height
    const HELIX_RADIUS = 50 // Horizontal amplitude
    const ROTATION_SPEED = 0.8 // Radians per second
    const RUNGS = 14 // Number of base-pair connections
    const NODE_RADIUS = 3.5

    const draw = (now: number) => {
      const w = window.innerWidth
      const h = window.innerHeight
      const cx = w / 2
      const cy = h / 2
      const elapsed = (now - startTimeRef.current) / 1000
      const rotation = elapsed * ROTATION_SPEED

      ctx.clearRect(0, 0, w, h)

      // Exit animation
      let exitProgress = 0
      if (isExiting) {
        if (exitStartRef.current === null) exitStartRef.current = now
        exitProgress = Math.min((now - exitStartRef.current) / 700, 1)
      }

      const globalAlpha = 1 - exitProgress
      const exitScale = 1 + exitProgress * 0.3 // helix expands outward
      const exitSpinBoost = exitProgress * 8 // spin faster on exit

      const helixH = h * HELIX_HEIGHT_RATIO * exitScale
      const startY = cy - helixH / 2
      const dy = helixH / HELIX_POINTS

      // Compute strand points
      const strand1: { x: number; y: number; z: number }[] = []
      const strand2: { x: number; y: number; z: number }[] = []

      for (let i = 0; i <= HELIX_POINTS; i++) {
        const t = i / HELIX_POINTS
        const angle = t * Math.PI * 4 + rotation + exitSpinBoost
        const y = startY + i * dy
        const r = HELIX_RADIUS * (1 + exitProgress * 0.5)

        // Strand 1
        const x1 = cx + Math.cos(angle) * r
        const z1 = Math.sin(angle)
        strand1.push({ x: x1, y, z: z1 })

        // Strand 2 (offset by PI)
        const x2 = cx + Math.cos(angle + Math.PI) * r
        const z2 = Math.sin(angle + Math.PI)
        strand2.push({ x: x2, y, z: z2 })
      }

      // ── Draw rungs (base-pair connections) ──
      const rungStep = Math.floor(HELIX_POINTS / RUNGS)
      for (let i = 0; i < RUNGS; i++) {
        const idx = Math.floor(rungStep * i + rungStep / 2)
        if (idx >= strand1.length) continue

        const p1 = strand1[idx]
        const p2 = strand2[idx]

        // Depth-based opacity (front rungs brighter)
        const avgZ = (p1.z + p2.z) / 2
        const depthAlpha = 0.15 + (avgZ + 1) * 0.15

        // Gradient rung
        const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y)
        grad.addColorStop(0, `rgba(${rgb1.r}, ${rgb1.g}, ${rgb1.b}, ${depthAlpha * globalAlpha})`)
        grad.addColorStop(0.5, `rgba(${rgb1.r}, ${rgb1.g}, ${rgb1.b}, ${depthAlpha * 0.4 * globalAlpha})`)
        grad.addColorStop(1, `rgba(${rgb2.r}, ${rgb2.g}, ${rgb2.b}, ${depthAlpha * globalAlpha})`)

        ctx.strokeStyle = grad
        ctx.lineWidth = 1.5
        ctx.beginPath()
        ctx.moveTo(p1.x, p1.y)
        ctx.lineTo(p2.x, p2.y)
        ctx.stroke()
      }

      // ── Draw strands with depth ──
      // We need to draw back-to-front for proper overlapping
      // Sort segments by z depth and draw accordingly

      const drawStrand = (
        points: { x: number; y: number; z: number }[],
        rgb: { r: number; g: number; b: number },
        backRgb: { r: number; g: number; b: number },
      ) => {
        // Draw the continuous line
        for (let i = 0; i < points.length - 1; i++) {
          const p = points[i]
          const pNext = points[i + 1]
          const avgZ = (p.z + pNext.z) / 2

          // Depth: z ranges -1 to 1. Map to opacity
          const depthAlpha = 0.2 + (avgZ + 1) * 0.4
          const lineWidth = 1.5 + (avgZ + 1) * 1

          // Blend colors based on depth (front = primary, back = faded)
          const blendedRgb = avgZ > 0 ? rgb : backRgb
          const alpha = depthAlpha * globalAlpha

          ctx.strokeStyle = `rgba(${blendedRgb.r}, ${blendedRgb.g}, ${blendedRgb.b}, ${alpha})`
          ctx.lineWidth = lineWidth
          ctx.lineCap = 'round'
          ctx.beginPath()
          ctx.moveTo(p.x, p.y)
          ctx.lineTo(pNext.x, pNext.y)
          ctx.stroke()
        }

        // Draw nodes at regular intervals
        const nodeStep = Math.floor(HELIX_POINTS / 16)
        for (let i = 0; i < points.length; i += nodeStep) {
          const p = points[i]
          const depthAlpha = 0.3 + (p.z + 1) * 0.35
          const r = NODE_RADIUS * (0.6 + (p.z + 1) * 0.4)
          const alpha = depthAlpha * globalAlpha

          ctx.beginPath()
          ctx.arc(p.x, p.y, r, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`
          ctx.fill()

          // Glow on front-facing nodes
          if (p.z > 0.3) {
            ctx.beginPath()
            ctx.arc(p.x, p.y, r * 2.5, 0, Math.PI * 2)
            const glowGrad = ctx.createRadialGradient(
              p.x, p.y, 0,
              p.x, p.y, r * 2.5,
            )
            glowGrad.addColorStop(0, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.15 * globalAlpha})`)
            glowGrad.addColorStop(1, `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`)
            ctx.fillStyle = glowGrad
            ctx.fill()
          }
        }
      }

      // Faded "back" color
      const backRgb1 = prefersDark
        ? { r: Math.round(rgb1.r * 0.4), g: Math.round(rgb1.g * 0.4), b: Math.round(rgb1.b * 0.4) }
        : { r: Math.round(rgb1.r * 0.6 + 100), g: Math.round(rgb1.g * 0.6 + 100), b: Math.round(rgb1.b * 0.6 + 100) }
      const backRgb2 = prefersDark
        ? { r: Math.round(rgb2.r * 0.4), g: Math.round(rgb2.g * 0.4), b: Math.round(rgb2.b * 0.4) }
        : { r: Math.round(rgb2.r * 0.6 + 100), g: Math.round(rgb2.g * 0.6 + 100), b: Math.round(rgb2.b * 0.6 + 100) }

      drawStrand(strand1, rgb1, backRgb1)
      drawStrand(strand2, rgb2, backRgb2)

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)

    return () => {
      window.removeEventListener('resize', resize)
      cancelAnimationFrame(rafRef.current)
    }
  }, [canvasRef, primaryColor, secondaryColor, prefersDark, isExiting])
}

// ── Component ────────────────────────────────────────────────────

export function Preloader({ onComplete, duration = 3000 }: PreloaderProps) {
  const { branding } = useBrandingStore()
  const [isExiting, setIsExiting] = useState(false)
  const [isVisible, setIsVisible] = useState(true)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  const primaryColor = branding.colors.primary || '#3B82F6'
  const secondaryColor = branding.colors.secondary || '#64748B'
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  const logoSrc = prefersDark ? branding.logos.dark : branding.logos.light

  const handleComplete = useCallback(() => {
    setIsVisible(false)
    onComplete()
  }, [onComplete])

  useEffect(() => {
    const exitTimer = setTimeout(() => setIsExiting(true), duration)
    const removeTimer = setTimeout(handleComplete, duration + 750)
    return () => {
      clearTimeout(exitTimer)
      clearTimeout(removeTimer)
    }
  }, [duration, handleComplete])

  useHelixCanvas(canvasRef, primaryColor, secondaryColor, prefersDark, isExiting)

  if (!isVisible) return null

  const appName = branding.appName || BRANDING.appName
  const tagline = branding.tagline || ''
  const bg = prefersDark ? '#09090b' : '#ffffff'
  const textColor = prefersDark ? '#fafafa' : '#111111'
  const subtextColor = prefersDark ? '#a1a1aa' : '#71717a'

  return (
    <>
      <style>{`
        @keyframes pl-logo-emerge {
          0% {
            opacity: 0;
            transform: scale(0.7) rotateY(90deg);
            filter: blur(8px);
          }
          50% {
            transform: scale(1.03) rotateY(-5deg);
            filter: blur(1px);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotateY(0deg);
            filter: blur(0);
          }
        }
        @keyframes pl-glow-breathe {
          0%, 100% {
            box-shadow: 0 0 35px 10px ${primaryColor}20,
                        0 0 70px 25px ${primaryColor}10;
          }
          50% {
            box-shadow: 0 0 55px 18px ${primaryColor}30,
                        0 0 110px 45px ${primaryColor}18;
          }
        }
        @keyframes pl-letter {
          0% {
            opacity: 0;
            transform: translateY(16px) scale(0.8) rotateX(40deg);
            filter: blur(4px);
          }
          65% {
            transform: translateY(-2px) scale(1.03) rotateX(-3deg);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1) rotateX(0deg);
            filter: blur(0);
          }
        }
        @keyframes pl-dot {
          0% {
            opacity: 0;
            transform: scale(0);
          }
          100% {
            opacity: 0.5;
            transform: scale(1);
          }
        }
        @keyframes pl-tagline-char {
          0% {
            opacity: 0;
            transform: translateY(8px);
            filter: blur(3px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
            filter: blur(0);
          }
        }
        @keyframes pl-progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        @keyframes pl-content-exit {
          0% {
            opacity: 1;
            transform: scale(1);
            filter: blur(0);
          }
          100% {
            opacity: 0;
            transform: scale(1.12);
            filter: blur(10px);
          }
        }
      `}</style>

      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          backgroundColor: bg,
          overflow: 'hidden',
          perspective: '800px',
        }}
      >
        {/* ── Helix canvas ── */}
        <canvas
          ref={canvasRef}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        />

        {/* ── Center content ── */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
            animation: isExiting
              ? 'pl-content-exit 0.7s cubic-bezier(0.4, 0, 0.2, 1) forwards'
              : undefined,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            {/* ── Glow backdrop ── */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                animation: 'pl-glow-breathe 2.8s ease-in-out infinite',
                pointerEvents: 'none',
              }}
            />

            {/* ── Frosted glass card behind content ── */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '450px',
                height: '180px',
                borderRadius: '20px',
                background: prefersDark
                  ? 'rgba(9, 9, 11, 0.65)'
                  : 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                border: prefersDark
                  ? '1px solid rgba(255,255,255,0.06)'
                  : '1px solid rgba(0,0,0,0.06)',
                pointerEvents: 'none',
              }}
            />

            {logoSrc ? (
              /* ── Logo ── */
              <div
                style={{
                  position: 'relative',
                  opacity: 0,
                  animation:
                    'pl-logo-emerge 1s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards',
                }}
              >
                <img
                  src={logoSrc}
                  alt={appName}
                  style={{ height: '52px', display: 'block' }}
                />
              </div>
            ) : (
              /* ── Animated app name with dot separators ── */
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  position: 'relative',
                }}
              >
                {appName.split('').map((letter, i) => (
                  <span key={i} style={{ display: 'contents' }}>
                    {i > 0 && (
                      <span
                        style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: primaryColor,
                          margin: '0 10px',
                          verticalAlign: 'middle',
                          opacity: 0,
                          transform: 'scale(0)',
                          animation: `pl-dot 0.35s ease-out ${0.6 + i * 0.15}s forwards`,
                        }}
                      />
                    )}
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: '52px',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        color: textColor,
                        opacity: 0,
                        animation: `pl-letter 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) ${0.4 + i * 0.15}s forwards`,
                      }}
                    >
                      {letter}
                    </span>
                  </span>
                ))}
              </div>
            )}

            {/* ── Tagline (letter by letter) ── */}
            {tagline && (
              <p
                style={{
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  fontWeight: 400,
                  letterSpacing: '0.25em',
                  margin: 0,
                  position: 'relative',
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '0.5px',
                }}
              >
                {tagline.split('').map((char, i) => (
                  <span
                    key={i}
                    style={{
                      opacity: 0,
                      color: subtextColor,
                      display: 'inline-block',
                      whiteSpace: char === ' ' ? 'pre' : undefined,
                      animation: `pl-tagline-char 0.3s ease-out ${1.6 + i * 0.04}s forwards`,
                    }}
                  >
                    {char}
                  </span>
                ))}
              </p>
            )}
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            zIndex: 2,
            backgroundColor: prefersDark
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.05)',
          }}
        >
          <div
            style={{
              height: '100%',
              borderRadius: '0 2px 2px 0',
              background: `linear-gradient(90deg, ${primaryColor}88, ${primaryColor}, ${secondaryColor}88)`,
              animation: `pl-progress ${duration}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`,
            }}
          />
        </div>
      </div>
    </>
  )
}
